import React, { useState, useEffect, useMemo } from "react";
import { Spin } from "antd";
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notifyUser, validateGhanaPhone } from "../../../util/util";
import { extractErrorMessage } from '../../../hooks/extractErrorMessage';
import { authApi } from "../../../services/auth.service";
import { ReactComponent as LockIcon } from '../../../assets/UiElements/lock.svg';
import { ghanaCardUrl } from "../../../util/baseUrl";

// Renamed to GhanaCardVerificationPage
export const GhanaCardVerificationPage = () => {
  // const [iframeKey, setIframeKey] = useState(0); // removed, not needed for new tab flow
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1); // 1: Ghana card, 2: Photo/biometric, 3: Create account, 4: OTP
  const [loading, setLoading] = useState(false);
  const [ghanaCardNumber, setGhanaCardNumber] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [accountForm, setAccountForm] = useState({ email: "", phone: "", password: "", c_password: "" });
  const [accountErrors, setAccountErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [seconds, setSeconds] = useState(165); // 2:45 min
  const [shake, setShake] = useState(false);
  const [phoneForOtp, setPhoneForOtp] = useState("");
  const [otpRefForResend, setOtpRefForResend] = useState(""); // Store otp_ref from create account
  const [mounted, setMounted] = useState(false);

  // Delay rendering of omk-text-field until after mount
  useEffect(() => {
    setMounted(true);
  }, []);
  // const [redirectUrl, setRedirectUrl] = useState("");
  // const [modalOpen, setModalOpen] = useState(false); // removed, not needed for new tab flow

  // ID Card input handler: allow any country prefix (2-3 letters), auto-insert hyphens, exactly 8 digits + 1 digit, no extra chars
  const handleGhanaCardInput = (e) => {
    let value = e.target.value.replace(/[^0-9A-Za-z-]/g, '');
    // Extract prefix if user typed it (2-3 letters before first hyphen)
    const prefixMatch = value.match(/^([A-Za-z]{2,3})-?/);
    let prefix = prefixMatch ? prefixMatch[1].toUpperCase() : '';
    // Get the rest after prefix
    let rest = prefixMatch ? value.slice(prefixMatch[0].length) : value;
    // Remove all hyphens from the rest
    rest = rest.replace(/-/g, '');
    // Only allow up to 9 digits, 1 digit after last hyphen, and nothing more
    let main = rest.slice(0, 9);
    let suffix = rest.slice(9, 10); // Only 1 digit after last hyphen
    let formatted = '';
    if (main.length > 0) {
      formatted = prefix ? `${prefix}-${main}` : main;
      if (suffix.length > 0) {
        formatted += `-${suffix}`;
      }
    } else if (prefix) {
      formatted = prefix + '-';
    }
    setGhanaCardNumber(formatted);
  };

  // Step 1: Ghana Card Verification (open new tab, listen for message)
  React.useEffect(() => {
    // Listen for postMessage from verification tab
    function handleMessage(event) {
      // You may want to check event.origin for security
      if (event.data) {
        let token = null;
        
        // Handle the JSON response structure from Ghana Card verification
        if (event.data.success && event.data.data && event.data.data.data && event.data.data.data.verification_token) {
          token = event.data.data.data.verification_token;
        }
        // Fallback for other possible formats
        else if (event.data.enc) {
          token = event.data.enc;
        }
        else if (event.data.ghanaCardVerificationToken) {
          token = event.data.ghanaCardVerificationToken;
        }
        else if (event.data.verification_token) {
          token = event.data.verification_token;
        }
        
        if (token) {
          setVerificationToken(token);
          setLoading(false);
          setStep(3);
          notifyUser('success', 'Ghana Card verified successfully!', 'Success');
        }
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  React.useEffect(() => {
    // Check for verification_token in query params (redirect flow)
    const params = new URLSearchParams(location.search);
    const token = params.get('verification_token');
    const status = params.get('status');
    if (token && status === 'success') {
      setVerificationToken(token);
      setStep(3);
      setLoading(false);
      notifyUser('success', 'Ghana Card verified successfully!', 'Success');
    }
  }, [location.search]);

  const handleGhanaCardVerify = async () => {
    // Validate ID Card format: XXX-XXXXXXXXX-X (2-3 letter prefix, 9 digits, 1 digit after last hyphen, nothing after)
    const match = /^[A-Z]{2,3}-\d{9}-\d{1}$/.test(ghanaCardNumber);
    if (!match) {
  notifyUser('error', 'Ghana Card number must be in the format XXX-XXXXXXXXX-X (2-3 letter prefix, 9 digits, 1 digit after last hyphen, e.g. GHA-800818786-7), with nothing after the last digit.', 'Validation Error.');
      return;
    }
  // Submit the POST in the current tab for uniformity
    const form = document.createElement('form');
    form.action = ghanaCardUrl;
    form.method = 'POST';
  form.target = '_self'; // Use current tab
  form.style.display = 'none';
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'ghana_card_number';
  input.value = ghanaCardNumber;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
  setLoading(true);
    // The effect above will handle the message from the verification tab
  };

  // Step 2: Photo/Biometric Verification (user must complete in new tab, then paste token)
  // const [manualToken, setManualToken] = useState("");
  // const handleManualTokenSubmit = () => {};

  // Step 3: Create Account

  // Password criteria
  const password = accountForm.password;
  const confirmPassword = accountForm.c_password;
  const criteria = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    hasNumber: /\d/.test(password),
  };
  const isPasswordValid = Object.values(criteria).every(Boolean);
  // Phone validation using centralized utilities
  const isPhoneValid = (val) => {
    return validateGhanaPhone(val).isValid;
  };

  const isAccountButtonDisabled = !accountForm.email || !accountForm.phone || !password || !confirmPassword || loading || !isPhoneValid(accountForm.phone);
  const handleCreateAccount = async () => {
    if (!isPasswordValid) {
  notifyUser('error', 'Password must match all requirements', 'Validation Error');
      return;
    }
    if (password !== confirmPassword) {
  notifyUser('error', 'Password must match password confirmation', 'Validation Error');
      return;
    }
    // Client-side phone validation (handles 05XXXXXXXX → 233XXXXXXXX)
    const phoneValidation = validateGhanaPhone(accountForm.phone);
    if (!phoneValidation.isValid) {
      setAccountErrors(prev => ({ ...prev, phone: [phoneValidation.error] }));
      notifyUser('error', phoneValidation.error, 'Validation Error');
      return;
    }
    
    // Update form with normalized phone number before submission
    const normalizedPhone = phoneValidation.normalized;
    setLoading(true);
    // Clear previous errors
    setAccountErrors({});
    try {
      const response = await authApi.createAccount({ ...accountForm, phone: normalizedPhone, verification_token: verificationToken });
      // Success -> move to OTP step
      // Capture otp_ref from response if available
      const otpRef = response.data?.data?.otp_ref || response.data?.otp_ref || "";
      setOtpRefForResend(otpRef);
      setPhoneForOtp(accountForm.phone);
      setStep(4);
    } catch (e) {
      // If server returned validation errors in the shape { message: 'Validation Error.', data: { field: [msg] } }
      const respData = e?.response?.data;
      if (respData && respData.data && typeof respData.data === 'object') {
        setAccountErrors(respData.data);
        // Build a concise message from field errors (prefer field messages over generic message)
        const fieldMessages = Object.keys(respData.data)
          .map((k) => {
            const v = respData.data[k];
            if (Array.isArray(v) && v.length) return `${k}: ${v[0]}`;
            return null;
          })
          .filter(Boolean)
          .join(' • ');

        notifyUser('error', fieldMessages || respData.message || 'Validation Error', 'Validation Error');
      } else {
        notifyUser('error', extractErrorMessage(e, 'Account creation failed'), 'Error');
      }
    }
    setLoading(false);
  };

  // Clear field error when user edits an input
  const handleAccountChangeWithErrorClear = (e) => {
    const { name, value } = e.target;
    setAccountForm(prev => ({ ...prev, [name]: value }));
    if (accountErrors && accountErrors[name]) {
      setAccountErrors(prev => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  // OTP timer and resend logic
  const formatTime = useMemo(() => {
    return (timeInSeconds) => {
      const minutes = Math.floor(timeInSeconds / 60);
      const seconds = timeInSeconds % 60;
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };
  }, []);
  const remainingTime = useMemo(() => {
    return seconds > 45 ? formatTime(seconds - 45) : null;
  }, [seconds, formatTime]);
  useEffect(() => {
    if (step !== 4) return;
    if (seconds <= 0) return;
    const timer = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [seconds, step]);

  const changeHandler = (e) => setOtp(e.target.value);

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
  notifyUser('error', 'Please enter the 6-digit OTP received to proceed!', 'Validation Error');
      return;
    }
    setLoading(true);
    try {
      // Use otp_ref if available, otherwise use phone
      const payload = otpRefForResend ? { otp_ref: otpRefForResend, otp } : { phone: phoneForOtp, otp };
      await authApi.verifyOtp(payload);
      notifyUser("success", "Account created and verified! You can now log in.", "Success");
      navigate("/onboard/login");
    } catch (e) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
  notifyUser('error', e?.response?.data?.message || extractErrorMessage(e, 'OTP verification failed'), 'Error');
    }
    setLoading(false);
  };

  const resendOTP = async () => {
    if (seconds > 45) return;
    setShake(true);
    setSeconds(165);
    setTimeout(() => setShake(false), 500);
    try {
      // Always use otp_ref if available, otherwise fall back to phone
      const identifier = otpRefForResend || phoneForOtp;
      const response = await authApi.resendOtp(identifier);
      // Update otp_ref from response if available (new otp_ref for subsequent calls)
      const newOtpRef = response.data?.data?.otp_ref;
      if (newOtpRef) {
        setOtpRefForResend(newOtpRef);
      }
      notifyUser("success", response.data?.message || "OTP has been resent successfully!", "Success");
    } catch (e) {
      notifyUser('error', extractErrorMessage(e, 'Failed to resend OTP'), 'Error');
    }
  };

  return (
    <div className='mx-auto mobile-full-width shadow bg-white p-5 rounded login-width'>
      <div align='center'>
        {/* Step 1: Ghana Card Number */}
        {step === 1 && (
          <>
            <p className='lead fw-bold'>Ghana Card Verification</p>
            <div className="mt-4 w-75 mx-auto mobile-full-width">
              <Spin spinning={loading}>
                {mounted && (
                  <omk-text-field
                    label="Ghana Card Number"
                    value={ghanaCardNumber}
                    type="text"
                    class="mb-4"
                    onInput={handleGhanaCardInput}
                    name="ghana_card_number"
                    placeholder="Enter your Ghana Card Number (e.g., GHA-800818786-7)"
                    style={{ marginBottom: '20px', borderRadius: '50px', padding: '10px' }}
                  />
                )}
                <p className="text-center omk-text-small text-black-50"><LockIcon style={{ marginRight: '5px', width: '16px', height: '16px' }} /> Your information is confidential and secure</p>
                <button
                  className={`btn ${!ghanaCardNumber ? 'btn-secondary' : 'btn-primary'} mb-4 w-50 mobile-full-width`}
                  disabled={!ghanaCardNumber || loading}
                  style={{ borderRadius: '50px', padding: !ghanaCardNumber ? '5px 20px' : '10px 20px', height: 'auto' }}
                  onClick={handleGhanaCardVerify}
                >
                  {loading ? 'Verifying...' : 'Continue'}
                </button>
                {/* Verification now opens in a new tab and uses postMessage for callback */}
              </Spin>
            </div>
          </>
        )}
        {/* Step 2: Photo/Biometric Verification (handled in new tab, auto-advance on message) */}
        {/* Step 3: Create Account */}
        {step === 3 && (
          <>
            <p className='lead fw-bold'>Create Account</p>
            <div className="mt-4 w-75 mx-auto mobile-full-width">
              <Spin spinning={loading}>
                {mounted && (
                  <omk-text-field
                    label="Email"
                    value={accountForm.email}
                    type="email"
                    class="mb-4"
                    onInput={handleAccountChangeWithErrorClear}
                    name="email"
                    placeholder="Enter your email"
                    style={{ marginBottom: '20px', borderRadius: '50px', padding: '10px' }}
                  />
                )}
                {accountErrors?.email && (
                  <div className="text-danger small mt-1">{accountErrors.email[0]}</div>
                )}
                {mounted && (
                  <omk-text-field
                    label="Phone"
                    value={accountForm.phone}
                    type="tel"
                    class="mb-4"
                    onInput={handleAccountChangeWithErrorClear}
                    name="phone"
                    placeholder="05XXXXXXXX or 233XXXXXXXXX"
                    style={{ marginBottom: '20px', borderRadius: '50px', padding: '10px' }}
                  />
                )}
                {accountErrors?.phone ? (
                  <div className="text-danger small mt-1">{accountErrors.phone[0]}</div>
                ) : (
                  accountForm.phone && !isPhoneValid(accountForm.phone) && (
                    <div className="text-danger small mt-1">Phone must start with '233' and be 12 digits (e.g., 233537144161)</div>
                  )
                )}
                <div className="position-relative mb-4">
                  {mounted && (
                    <omk-text-field
                      label="Password"
                      value={password}
                      type={showPassword ? "text" : "password"}
                      class="mb-4"
                      onInput={handleAccountChangeWithErrorClear}
                      name="password"
                      placeholder="Enter your password"
                      style={{ marginBottom: '20px', borderRadius: '50px', padding: '10px' }}
                    />
                  )}
                  <button
                    type="button"
                    className="btn btn-link position-absolute"
                    style={{
                      right: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'none',
                      zIndex: 10
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} color="#666" /> : <Eye size={20} color="#666" />}
                  </button>
                </div>

                {/* Password criteria UI */}
                {password && (
                  <div className="row mb-4">
                    <div className="col-6">
                      <small className="float-start">
                        {criteria.minLength ? (
                          <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                        ) : (
                          <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                        )}
                        Min 8 characters
                      </small><br />
                      <small className="float-start">
                        {criteria.hasUppercase ? (
                          <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                        ) : (
                          <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                        )}
                        An uppercase
                      </small>
                    </div>
                    <div className="col-6">
                      <small className="float-start">
                        {criteria.hasSpecialChar ? (
                          <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                        ) : (
                          <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                        )}
                        A special character
                      </small><br />
                      <small className="float-start">
                        {criteria.hasNumber ? (
                          <i className="bi bi-check-lg text-success" style={{ marginRight: '5px' }} />
                        ) : (
                          <i className="bi bi-x-lg text-danger" style={{ marginRight: '5px' }} />
                        )}
                        A number
                      </small>
                    </div>
                  </div>
                )}

                <div className="position-relative mb-4">
                  {mounted && (
                    <omk-text-field
                        label="Confirm Password"
                        value={confirmPassword}
                        type={showConfirmPassword ? "text" : "password"}
                        class="mb-4"
                        onInput={handleAccountChangeWithErrorClear}
                        name="c_password"
                        placeholder="Confirm your password"
                        style={{ marginBottom: '20px', borderRadius: '50px', padding: '10px' }}
                      />
                  )}
                  <button
                    type="button"
                    className="btn btn-link position-absolute"
                    style={{
                      right: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'none',
                      zIndex: 10
                    }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={20} color="#666" /> : <Eye size={20} color="#666" />}
                  </button>
                </div>
                <p className="text-center omk-text-small text-black-50"><LockIcon style={{ marginRight: '5px', width: '16px', height: '16px' }} /> Your information is confidential and secure</p>
                <button
                  className={`btn ${isAccountButtonDisabled ? 'btn-secondary' : 'btn-primary'} mb-4 w-50 mobile-full-width`}
                  disabled={isAccountButtonDisabled}
                  style={{ borderRadius: '50px', padding: isAccountButtonDisabled ? '5px 20px' : '10px 20px', height: 'auto' }}
                  onClick={handleCreateAccount}
                >
                  {loading ? 'Creating...' : 'Continue'}
                </button>
              </Spin>
            </div>
          </>
        )}
        {/* Step 4: OTP Verification */}
        {step === 4 && (
          <>
            <p className='lead fw-bold'>Enter your One Time Pin</p>
            <small className="text-center">
              We'll send a One-Time Pin(OTP) to {phoneForOtp?.slice(0, 6)}****
            </small>
            <Spin spinning={loading}>
              <div className="mt-4 w-75 mx-auto mobile-full-width">
                <omk-otp-field
                  number-of-inputs="6"
                  resend-text=""
                  resend-button-label=""
                  label="One-time Password"
                  className={`mb-1 ${shake ? "shake" : ""}`}
                  value={otp}
                  onInput={changeHandler}
                />
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <p className="text-center omk-text-small text-black-100 mb-1">
                    Didn't receive the OTP?&nbsp;
                  </p>
                  <div className="text-center">
                    {remainingTime ? (
                      <p className="text-center omk-text-small text-black-50 mb-1">
                        Resend in {remainingTime}
                      </p>
                    ) : (
                      <p
                        className="text-center omk-text-small mb-1"
                        style={{
                          color: "#28a745",
                          fontWeight: "bold",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={resendOTP}
                      >
                        Resend OTP
                      </p>
                    )}
                  </div>
                </div>
                <div className="d-flex justify-content-center">
                  <p className="text-center omk-text-small text-black-50 mb-5">
                    <LockIcon style={{ marginRight: "5px", width: "16px", height: "16px" }} />
                    Your information is confidential and secure
                  </p>
                </div>
                <button
                  onClick={handleVerifyOtp}
                  className={`btn ${otp.length !== 6 ? "btn-secondary" : "btn-primary"} mb-4 w-50 mobile-full-width`}
                  disabled={otp.length !== 6 || loading}
                  style={{
                    borderRadius: "50px",
                    padding: otp.length !== 6 ? "5px 20px" : "10px 20px",
                    height: "auto",
                  }}
                >
                  {loading ? "Verifying..." : "Continue"}
                </button>
              </div>
            </Spin>
            <style>{`
                @keyframes shake {
                    0% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    50% { transform: translateX(5px); }
                    75% { transform: translateX(-5px); }
                    100% { transform: translateX(0); }
                }
                .shake {
                    animation: shake 0.5s ease-out;
                }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
}